import * as actionTypes from './actionTypes';

/*------------------------------------------------------------------------------------------------*/
/* Zuora Credential Actions                                                                       */
/*------------------------------------------------------------------------------------------------*/
/**
 * fetchZuoraCredentials
 * Get Zuora Credentials needed to render the Zuora hosted payments form
 */
export const fetchZuoraCredentials = () => ({
  type: actionTypes.FETCH_ZUORA_CREDENTIALS,
});

/**
 * receiveZuoraCredentials
 * Receive credentials neede for Zuora api calls
 * @param { Object } credentials: Object containing relevant zuora credentials
 */
export const receiveZuoraCredentials = (credentials) => ({
  type: actionTypes.FETCH_ZUORA_CREDENTIALS_RESPONSE,
  credentials,
});

/**
 * receiveZuoraCredentialsError
 * The fetch for zuora credentials was unsuccesful
 * @param { Object } error: Object containing relevant error information
 */
export const receiveZuoraCredentialsError = (error) => ({
  type: actionTypes.FETCH_ZUORA_CREDENTIALS_ERROR,
  error,
});

/**
 * creditCardUpdateSuccessful
 * Setting the new default credit card was successful
 */
export const creditCardUpdateSuccessful = () => ({
  type: actionTypes.CREDIT_CARD_UPDATE_SUCCESSFUL,
});

/**
 * creditCardUpdateError
 * Setting the new default credit card was unsuccessful
 * @param { Object } error: Object containing relevant error information
 */
export const creditCardUpdateError = (error) => ({
  type: actionTypes.CREDIT_CARD_UPDATE_ERROR,
  error,
});

/**
 * updateDefaultPaymentMethodRequest
 * Triggers an API request to make the most recent Zuora payment entered the default payment
 * @param { String } zuoraAccountName: Name of the account that is being updated.
 */
export const updateDefaultPaymentMethodRequest = (zuoraAccountName) => ({
  type: actionTypes.UPDATE_DEFAULT_PAYMENT_METHOD_REQUEST,
  zuoraAccountName,
});

/**
 * updateDefaultPaymentMethodSuccess
 * Triggers a successful toast that the most recent payment method was made the default payment
 * method
 */
export const updateDefaultPaymentMethodSuccess = () => ({
  type: actionTypes.UPDATE_DEFAULT_PAYMENT_METHOD_SUCCESS,
});

/**
 * updateDefaultPaymentMethodError
 * Triggers a error toast that the default payment method call was unsuccessful
 * @param { Object } error: Object containing relevant error information
 */
export const updateDefaultPaymentMethodError = (error) => ({
  type: actionTypes.UPDATE_DEFAULT_PAYMENT_METHOD_ERROR,
  error,
});

/**
 * getTrialEndDateRequest
 * Triggers an API request to get the sales trial end date.
 */
export const getTrialEndDateRequest = () => ({
  type: actionTypes.FETCH_TRIAL_EXPIRATION_DATE_REQUEST,
});

/**
 * getTrialEndDateSuccess
 * Updates the state with the trial end date.
 * @param { Object } trialExpirationDateObject: Object containing the trial end date
 * and the payment plan id.
 */
export const getTrialEndDateSuccess = (trialExpirationDateObject) => ({
  type: actionTypes.FETCH_TRIAL_EXPIRATION_DATE_RESPONSE,
  ...trialExpirationDateObject,
});

/**
 * getTrialEndDateFailure
 * Triggers a error toast that the trial end date fetch was unsuccessful
 * @param { Object } error: Object containing relevant error information
 */
export const getTrialEndDateFailure = (error) => ({
  type: actionTypes.FETCH_TRIAL_EXPIRATION_DATE_ERROR,
  error,
});

/**
 * updateTrialEndDateRequest
 * Triggers an API request to update the sales trial end date.
 * @param { number } trialExpirationDate: New trial expiration date represented in millisseconds
 * or null.
 */
export const updateTrialEndDateRequest = (trialExpirationDate) => ({
  type: actionTypes.UPDATE_TRIAL_EXPIRATION_DATE_REQUEST,
  trialExpirationDate,
});

/**
 * updateTrialEndDateSuccess
 * Triggers a successful toast message that the expiration date was successfully updated.
 * @param { number } trialExpirationDate: New trial expiration date represented in millisseconds
 * or null.
 */
export const updateTrialEndDateSuccess = (trialExpirationDate) => ({
  type: actionTypes.UPDATE_TRIAL_EXPIRATION_DATE_RESPONSE,
  trialExpirationDate,
});

/**
 * updateTrialEndDateFailure
 * Triggers a error toast that the trial end date update was unsuccessful
 * @param { Object } error: Object containing relevant error information
 */
export const updateTrialEndDateFailure = (error) => ({
  type: actionTypes.UPDATE_TRIAL_EXPIRATION_DATE_ERROR,
  error,
});

/**
 * getCreditCardsRequest
 * Triggers an API request to fetch all credit cards on file.
 */
export const getCreditCardsRequest = () => ({
  type: actionTypes.GET_CREDIT_CARDS_REQUEST,
});

/**
 * getCreditCardsRequestSuccess
 * Update the state with the credit cards on file
 * @param { Object } creditCardInfo: Credit Cards on file.
 */
export const getCreditCardsSuccess = (creditCardInfo) => ({
  type: actionTypes.GET_CREDIT_CARDS_RESPONSE,
  creditCardInfo,
});

/**
 * getCreditCardsRequestFailure
 * Triggers an error if unable to retrieve credit cards on file
 * @param { Object } error: Object containing relevant error information
 */
export const getCreditCardsFailure = (error) => ({
  type: actionTypes.GET_CREDIT_CARDS_ERROR,
  error,
});

/**
 * addZuoraAccountRequest
 * Triggers an API request insert a new Zuora Account.
 * @param { Object } zuoraAccount: Zuora account information to be written into the database
 */
export const addZuoraAccountRequest = (zuoraAccount) => ({
  type: actionTypes.INSERT_ZUORA_ACCOUNT_REQUEST,
  zuoraAccount,
});

/**
 * addZuoraAccountSuccess
 * Triggers a successful Zuora account insert toast
 * @param { String } result: Result of Zuora account insert.
 */
export const addZuoraAccountSuccess = (result) => ({
  type: actionTypes.INSERT_ZUORA_ACCOUNT_SUCCESS,
  result,
});

/**
 * addZuoraAccountError
 * Triggers an error if unable to insert the zuora account
 * @param { Object } error: Object containing relevant error information
 */
export const addZuoraAccountError = (error) => ({
  type: actionTypes.INSERT_ZUORA_ACCOUNT_ERROR,
  error,
});

// ------------ SKU TABLE MODAL GET ACTIONS -----------------------------------------------------------------

/**
 * getZuoraProductsRequest
 * Triggers an API request to get Iterables Zuora Products.
 */
export const getZuoraProductsRequest = () => ({
  type: actionTypes.GET_ZUORA_PRODUCTS_REQUEST,
});

/**
 * getZuoraProductsSuccess
 * returns Iterables Zuora Products
 * @param { Object } result: Result of get Zuora product.
 */
export const getZuoraProductsSuccess = (result) => ({
  type: actionTypes.GET_ZUORA_PRODUCTS_SUCCESS,
  result,
});

/**
 * getZuoraRatePlansRequest
 * Triggers an API request to get Iterables Zuora RatePlans.
 * @param { Object } ratePlanInfo: contains productId, pageSize, pageNum, getTotal
 */
export const getZuoraRatePlansRequest = ({ productId, pageSize, pageNum, getTotal }) => ({
  type: actionTypes.GET_ZUORA_RATEPLANS_REQUEST,
  productId,
  pageNum,
  pageSize,
  getTotal,
});

/**
 * getZuoraRatePlansSuccess
 * returns Iterables Zuora RatePlans
 * @param { Object } result: Result of get Zuora rateplans.
 */
export const getZuoraRatePlansSuccess = (data) => ({
  type: actionTypes.GET_ZUORA_RATEPLANS_SUCCESS,
  data,
});

// ------------ SKU TABLE MODAL UPDATE ACTIONS -----------------------------------------------------------------

/**
 * createZuoraObjectRequest
 * Triggers an API request to create a Zuora Object.
 * @param { Object } params: contains object info, type, and the refresh func
 */
export const createZuoraObjectRequest = ({ objectInfo, objectType, refresh }) => ({
  type: actionTypes.CREATE_ZUORA_OBJECT_REQUEST,
  objectInfo,
  objectType,
  refresh,
});

/**
 * updateZuoraObjectRequest
 * Triggers an API request to update a Zuora Object.
 * @param { Object } params: contains object info, type, and the refresh func
 */
export const updateZuoraObjectRequest = ({ objectInfo, objectType, refresh }) => ({
  type: actionTypes.UPDATE_ZUORA_OBJECT_REQUEST,
  objectInfo,
  objectType,
  refresh,
});

/**
 * deleteZuoraObjectRequest
 * Triggers an API request to delete a Zuora Object.
 * @param { Object } params: contains object info, type, and the refresh func
 */
export const deleteZuoraObjectRequest = ({ objectId, objectType, refresh }) => ({
  type: actionTypes.DELETE_ZUORA_OBJECT_REQUEST,
  objectId,
  objectType,
  refresh,
});

/**
 * objectManipulationFinished
 * returns if object manipulation had finished, success or fail
 */
export const objectManipulationFinished = () => ({
  type: actionTypes.OBJECT_MANIPULATION_FINISHED,
});
